// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The Live view's chiller-plant series, in one query.
 *
 * The counterpart to {@link useLiveLogicalNodeSeries}, and deliberately much
 * smaller: the fan-out across nine chillers, the timestamp reconciliation and
 * the cooling-load arithmetic all happen in the BFF, so there is one request
 * per poll rather than ten. Poll-window semantics live in
 * {@link useLiveComposedSeries}.
 */

import { getChillerPlant } from '../services/chillerPlantService';
import type { ChillerPlantData } from '../services/chillerPlantService';
import type { LiveSeriesCoverage } from './useLivePlanTelemetry';
import { EMPTY_SERIES, useLiveComposedSeries } from './useLiveComposedSeries';
import type { TimeAxis } from '../types';

interface ChillerPlantSeries {
  /**
   * Fleet cooling load in **thermal** kW, gated on run status, on the plan axis.
   *
   * Drawn by the Live page's "Cooling load (thermal)" card against the plan's
   * `total_cooling_load_kw` forecast. Gated means a chiller contributes only
   * where it is known to be running, so an idle unit is absent rather than a
   * zero; the ungated companion stays on
   * `data.charts.coolingLoad.totalAllChillers` and nothing draws it. This is
   * also what `coverage` reports against.
   */
  coolingLoad: (number | null)[];
  /**
   * Chillers running, reconstructed from change-of-value transitions.
   *
   * Integers only. Collapsing the ~1-minute feed onto the plan's 15-minute axis
   * takes the stage that held longest in each bucket, not the mean: 7 chillers
   * for eleven minutes and 8 for four is a bucket of 7, and 7.27 is not a state
   * the plant was ever in.
   */
  activeChillerCount: (number | null)[];
  /** Fleet-average chilled-water supply temperature, °F. */
  supplyTemp: (number | null)[];
}

export interface UseChillerPlantResult extends ChillerPlantSeries {
  data: ChillerPlantData | undefined;
  loading: boolean;
  coverage: LiveSeriesCoverage;
}

const EMPTY: ChillerPlantSeries = {
  coolingLoad: EMPTY_SERIES,
  activeChillerCount: EMPTY_SERIES,
  supplyTemp: EMPTY_SERIES,
};

export function useChillerPlant(
  siteId: string | undefined,
  timeAxis: TimeAxis | undefined
): UseChillerPlantResult {
  return useLiveComposedSeries<ChillerPlantData, ChillerPlantSeries>({
    name: 'chiller-plant',
    keyParts: [siteId],
    timeAxis,
    fetch: window => getChillerPlant(siteId as string, window),
    emptySeries: EMPTY,
    select: ({ charts }, { onAxis, onAxisDominant }) => {
      const coolingLoad = onAxis(charts.coolingLoad.values);
      return {
        primary: coolingLoad,
        series: {
          coolingLoad,
          activeChillerCount: onAxisDominant(charts.activeChillerCount.values),
          supplyTemp: onAxis(charts.chilledWaterTemps.supplyAvg.values),
        },
      };
    },
  });
}
